/**
 * User Service
 * Handles user-related API calls to the backend with comprehensive error handling
 * Migrated to use apiClient for automatic bearer token authentication
 */

import apiClient, { ApiError } from 'services/apiClient';
import {
  BackendUser,
  CreateUserRequest,
  GetUsersResponse,
  GetUsersParams,
  UserApiError,
  UserErrorType
} from 'types/user.types';

class UserService {
  /**
   * Helper method to convert ApiError to UserApiError
   */
  private static handleApiError(error: any, defaultMessage: string): UserApiError {
    if (error instanceof UserApiError) {
      return error;
    }

    const apiError = error as ApiError;
    const status = apiError.status || 0;
    const errorData = apiError.data;

    // Network errors
    if (!status || status === 0) {
      return new UserApiError(
        UserErrorType.NETWORK_ERROR,
        'Network error. Please check your internet connection.',
        0
      );
    }

    // Return UserApiError with details
    return new UserApiError(
      UserErrorType.UNKNOWN_ERROR,
      apiError.userMessage || defaultMessage,
      status,
      errorData
    );
  }

  /**
   * Create a new user in the backend
   */
  static async createUser(userData: CreateUserRequest): Promise<BackendUser> {
    try {
      console.log('[UserService] Creating user:', { email: userData.email, user_id: userData.user_id });

      const response = await apiClient.post<BackendUser>('/users', userData);

      console.log('[UserService] User created successfully');
      return response.data;

    } catch (error: any) {
      console.error('[UserService] Error creating user:', error);

      // Re-throw UserApiError as-is
      if (error instanceof UserApiError) {
        throw error;
      }

      // Handle specific error statuses
      const apiError = error as ApiError;
      const status = apiError.status || 0;
      const errorData = apiError.data;

      if (status === 409) {
        throw new UserApiError(
          UserErrorType.USER_ALREADY_EXISTS,
          'An account with this email or user ID already exists.',
          status,
          errorData
        );
      } else if (status === 422) {
        const validationMessage = errorData?.detail?.[0]?.msg || 'Please check your information and try again.';
        throw new UserApiError(
          UserErrorType.INVALID_DATA,
          `Invalid user data: ${validationMessage}`,
          status,
          errorData
        );
      } else if (status >= 500) {
        throw new UserApiError(
          UserErrorType.SERVER_ERROR,
          'Server error. Please try again later.',
          status,
          errorData
        );
      }

      // Handle all other errors
      throw this.handleApiError(error, 'Failed to create user. Please try again.');
    }
  }

  /**
   * Get user by ID
   */
  static async getUserById(userId: string): Promise<BackendUser> {
    try {
      console.log('[UserService] Fetching user:', userId);

      if (!userId || userId.trim() === '') {
        throw new UserApiError(
          UserErrorType.INVALID_DATA,
          'User ID is required.',
          400
        );
      }

      const response = await apiClient.get<BackendUser>(`/users/${encodeURIComponent(userId)}`);

      console.log('[UserService] User fetched successfully');
      return response.data;

    } catch (error: any) {
      console.error('[UserService] Error fetching user:', error);

      // Re-throw UserApiError as-is
      if (error instanceof UserApiError) {
        throw error;
      }

      // Handle specific error statuses
      const apiError = error as ApiError;
      const status = apiError.status || 0;
      const errorData = apiError.data;

      if (status === 404) {
        throw new UserApiError(
          UserErrorType.USER_NOT_FOUND,
          'User profile not found. Please try logging in again.',
          status,
          errorData
        );
      } else if (status >= 500) {
        throw new UserApiError(
          UserErrorType.SERVER_ERROR,
          'Server error. Please try again later.',
          status,
          errorData
        );
      }

      // Handle all other errors
      throw this.handleApiError(error, 'Failed to fetch user profile.');
    }
  }

  /**
   * Get all users with optional filters and pagination
   */
  static async getUsers(params?: GetUsersParams): Promise<GetUsersResponse> {
    try {
      console.log('[UserService] Fetching users');

      const response = await apiClient.get<GetUsersResponse>('/users', {
        params: params
      });

      console.log('[UserService] Users fetched successfully:', response.data.total, 'total');
      return response.data;

    } catch (error: any) {
      console.error('[UserService] Error fetching users:', error);

      // Re-throw UserApiError as-is
      if (error instanceof UserApiError) {
        throw error;
      }

      // Handle specific error statuses
      const apiError = error as ApiError;
      const status = apiError.status || 0;
      const errorData = apiError.data;

      if (status >= 500) {
        throw new UserApiError(
          UserErrorType.SERVER_ERROR,
          'Server error. Please try again later.',
          status,
          errorData
        );
      }

      // Handle all other errors
      throw this.handleApiError(error, 'Failed to fetch users.');
    }
  }

  /**
   * Delete a user account
   */
  static async deleteUser(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log('[UserService] Deleting user:', userId);

      if (!userId || userId.trim() === '') {
        throw new UserApiError(
          UserErrorType.INVALID_DATA,
          'User ID is required.',
          400
        );
      }

      const response = await apiClient.delete<{ success: boolean; user_id: string; message: string }>(
        `/users/${encodeURIComponent(userId)}`
      );

      console.log('[UserService] User deleted successfully');
      return {
        success: response.data.success,
        message: response.data.message
      };

    } catch (error: any) {
      console.error('[UserService] Error deleting user:', error);

      // Re-throw UserApiError as-is
      if (error instanceof UserApiError) {
        throw error;
      }

      // Handle specific error statuses
      const apiError = error as ApiError;
      const status = apiError.status || 0;
      const errorData = apiError.data;

      if (status === 404) {
        throw new UserApiError(
          UserErrorType.USER_NOT_FOUND,
          'User account not found.',
          status,
          errorData
        );
      } else if (status >= 500) {
        throw new UserApiError(
          UserErrorType.SERVER_ERROR,
          'Failed to delete user account. Please try again later.',
          status,
          errorData
        );
      }

      // Handle all other errors
      throw this.handleApiError(error, 'Failed to delete user account.');
    }
  }
}

export default UserService;
